import { bioAnalyticsDisabled } from '@/lib/link-in-bio/analytics-preference';
import { getConsentSnapshot, parseConsentCookieValue } from '@/lib/cookie-consent/consent';
import { sanitizeAnalyticsProperties } from '@/lib/analytics/event-safety';
import { isTelemetryProtectedRoute, isPublicBioRoute } from '@/lib/privacy/private-routes';

export const POSTHOG_PROXY_PATH = '/ingest';
export const POSTHOG_BROWSER_API_HOST = 'https://e.owlscran.com';
export const POSTHOG_UI_HOST = 'https://eu.posthog.com';
export const POSTHOG_EU_HOST = 'https://eu.i.posthog.com';
export const POSTHOG_EU_ASSETS_HOST = 'https://eu-assets.i.posthog.com';
export const STABLE_ID_COOKIE_NAME = 'statsig_stable_id';
export const POSTHOG_ADMIN_PATH_PREFIX = '/admin';

export type PostHogConsentState = 'pending' | 'accepted' | 'rejected';

type PostHogBrowserEvent = {
  event: string;
  properties?: Record<string, unknown>;
};

// Public SEO telemetry has a deliberately smaller envelope than product analytics.
// Filter after SDK enrichment so persisted attribution and person properties cannot leak.
const PUBLIC_TELEMETRY_SDK_PROPERTIES = new Set([
  'token',
  'distinct_id',
  '$device_id',
  '$session_id',
  '$window_id',
  '$anon_distinct_id',
  '$user_id',
  '$is_identified',
  '$process_person_profile',
  '$lib',
  '$lib_version',
  '$lib_custom_version',
  '$browser',
  '$browser_version',
  '$os',
  '$os_version',
  '$device_type',
  '$screen_height',
  '$screen_width',
  '$viewport_height',
  '$viewport_width',
]);
const PUBLIC_TOOL_PROPERTIES = new Set(['tool', 'action', 'format', 'variant']);

function filterPublicTelemetryProperties(event: PostHogBrowserEvent): Record<string, unknown> {
  if (event.event !== 'public_tool_output_used') return event.properties ?? {};
  return Object.fromEntries(
    Object.entries(event.properties ?? {}).filter(
      ([key, value]) =>
        (PUBLIC_TOOL_PROPERTIES.has(key) || PUBLIC_TELEMETRY_SDK_PROPERTIES.has(key)) &&
        (typeof value === 'string' ||
          typeof value === 'boolean' ||
          (typeof value === 'number' && Number.isFinite(value))),
    ),
  );
}

export function sanitizePostHogBrowserEvent<T extends PostHogBrowserEvent | null>(event: T): T {
  if (!event) return event;

  return {
    ...event,
    properties: sanitizeAnalyticsProperties({
      ...filterPublicTelemetryProperties(event),
      capture_source: 'browser',
    }),
  } as T;
}

type PostHogLike = {
  opt_in_capturing: () => void;
  opt_out_capturing: () => void;
  set_config: (config: {
    capture_exceptions?: boolean;
    autocapture?: boolean;
    capture_pageview?: false | 'history_change';
    capture_pageleave?: boolean;
    disable_session_recording: boolean;
    enable_recording_console_log?: boolean;
    logs?: { captureConsoleLogs?: boolean };
    tracing_headers?: string[];
  }) => void;
  startSessionRecording: (override?: true) => void;
  stopSessionRecording: () => void;
};

export function readCookieValue(cookieString: string, name: string): string | null {
  const prefix = `${name}=`;
  const part = cookieString
    .split(';')
    .map((segment) => segment.trim())
    .find((segment) => segment.startsWith(prefix));

  if (!part) return null;

  const encodedValue = part.slice(prefix.length);
  if (!encodedValue) return null;

  try {
    return decodeURIComponent(encodedValue);
  } catch {
    return null;
  }
}

export function getPostHogConsentState(cookieString: string): PostHogConsentState {
  const snapshot = getConsentSnapshot(readCookieValue(cookieString, 'owlscran_consent'));

  if (!snapshot.consent) return 'pending';
  return snapshot.telemetry.analyticsEnabled ? 'accepted' : 'rejected';
}

export function shouldIdentifyPostHogUser(cookieString: string): boolean {
  const consent = parseConsentCookieValue(readCookieValue(cookieString, 'owlscran_consent'));
  return Boolean(consent?.analytics);
}

export function buildPostHogInitConfig(input: {
  cookieString: string;
  hostname: string;
  isDevelopment: boolean;
  pathname: string;
  uiHost: string;
}) {
  const stableId = readCookieValue(input.cookieString, STABLE_ID_COOKIE_NAME);
  const replayEnabled = shouldEnablePostHogReplay(input.cookieString, input.pathname);
  const consentState = getPostHogConsentState(input.cookieString);
  const protectedRoute = isPostHogProtectedPath(input.pathname);
  const replayOnly = replayEnabled && consentState === 'rejected';

  return {
    api_host: POSTHOG_BROWSER_API_HOST,
    ui_host: input.uiHost,
    defaults: '2026-01-30' as const,
    capture_exceptions: !replayOnly,
    autocapture: !replayOnly,
    capture_pageview: replayOnly ? (false as const) : ('history_change' as const),
    capture_pageleave: !replayOnly,
    cookieless_mode: 'on_reject' as const,
    // Our Help panel sends Support tickets from the server, so never load the
    // posthog-js Support widget (it would fetch a script and log an event on
    // every page view once Support is enabled for the project).
    disable_conversations: true,
    debug: input.isDevelopment,
    // No surveys are launched or rendered by the app. Re-enable before adding one.
    disable_surveys: true,
    disable_session_recording: !replayEnabled,
    enable_recording_console_log: false,
    logs: {
      captureConsoleLogs: false,
    },
    tracing_headers: getPostHogTracingHeaderHosts(
      input.cookieString,
      input.pathname,
      input.hostname,
    ),
    opt_out_capturing_by_default: (consentState === 'rejected' && !replayEnabled) || protectedRoute,
    before_send: <T extends PostHogBrowserEvent | null>(event: T): T | null => {
      const cookie = typeof document === 'undefined' ? input.cookieString : document.cookie;
      const path = typeof window === 'undefined' ? input.pathname : window.location.pathname;
      // Replay shares the SDK transport with analytics. Read live preferences so
      // revocation and client-side navigation also gate events before React effects run.
      if (isPostHogProtectedPath(path) || (isPublicBioRoute(path) && bioAnalyticsDisabled()))
        return null;
      if (event?.event === '$snapshot') {
        if (!shouldEnablePostHogReplay(cookie, path)) return null;
      } else if (getPostHogConsentState(cookie) === 'rejected') {
        return null;
      }
      return sanitizePostHogBrowserEvent(event);
    },
    ...(stableId ? { bootstrap: { distinctID: stableId, isIdentifiedID: false } } : {}),
  };
}

export function isPostHogAdminPath(pathname: string): boolean {
  return pathname.startsWith(POSTHOG_ADMIN_PATH_PREFIX);
}

export function isPostHogProtectedPath(pathname: string): boolean {
  return isTelemetryProtectedRoute(pathname);
}

export function shouldEnablePostHogReplay(cookieString: string, pathname: string): boolean {
  const snapshot = getConsentSnapshot(readCookieValue(cookieString, 'owlscran_consent'));
  return (
    snapshot.telemetry.sessionRecordingEnabled &&
    !isPostHogAdminPath(pathname) &&
    !isPostHogProtectedPath(pathname)
  );
}

/**
 * Same-origin requests carry `X-POSTHOG-SESSION-ID` and `X-POSTHOG-DISTINCT-ID`
 * so server traces link to the person and replay. Only when the visitor allowed
 * analytics or replay, and never on routes excluded from browser telemetry.
 */
export function getPostHogTracingHeaderHosts(
  cookieString: string,
  pathname: string,
  hostname: string,
): string[] {
  if (!hostname || isPostHogProtectedPath(pathname)) return [];
  if (isPublicBioRoute(pathname) && bioAnalyticsDisabled()) return [];
  const snapshot = getConsentSnapshot(readCookieValue(cookieString, 'owlscran_consent'));
  const linked =
    snapshot.telemetry.analyticsEnabled || shouldEnablePostHogReplay(cookieString, pathname);
  return linked ? [hostname] : [];
}

export function syncPostHogConsent(
  posthog: PostHogLike,
  cookieString: string,
  pathname: string,
  hostname: string,
): void {
  const replayEnabled = shouldEnablePostHogReplay(cookieString, pathname);
  const snapshot = getConsentSnapshot(readCookieValue(cookieString, 'owlscran_consent'));

  const replayOnly = replayEnabled && !snapshot.telemetry.analyticsEnabled;
  posthog.set_config({
    capture_exceptions: !replayOnly,
    autocapture: !replayOnly,
    capture_pageview: replayOnly ? false : 'history_change',
    capture_pageleave: !replayOnly,
    disable_session_recording: !replayEnabled,
    enable_recording_console_log: false,
    logs: { captureConsoleLogs: false },
    tracing_headers: getPostHogTracingHeaderHosts(cookieString, pathname, hostname),
  });

  if (!replayEnabled) posthog.stopSessionRecording();

  // Private share URLs are bearer credentials and contain private business data.
  if (isPostHogProtectedPath(pathname)) {
    posthog.opt_out_capturing();
    return;
  }

  if (!snapshot.consent) return;

  // SDK opt-out destroys the recorder. Replay-only consent needs the transport
  // opted in, with analytics blocked by before_send and the capture settings above.
  if (snapshot.telemetry.analyticsEnabled || replayEnabled) {
    posthog.opt_in_capturing();
    if (replayEnabled) posthog.startSessionRecording(true);
    return;
  }

  posthog.opt_out_capturing();
}

export function getPostHogProxyDestination(pathname: string): URL | null {
  if (!pathname.startsWith(POSTHOG_PROXY_PATH)) return null;

  const base =
    pathname.startsWith(`${POSTHOG_PROXY_PATH}/static/`) ||
    pathname.startsWith(`${POSTHOG_PROXY_PATH}/array/`)
      ? POSTHOG_EU_ASSETS_HOST
      : POSTHOG_EU_HOST;

  return new URL(pathname.replace(POSTHOG_PROXY_PATH, '') || '/', base);
}
